import { useState } from 'react';
import { api } from '../api';
import { useLoad } from '../hooks';
import { duration, metaText, stamp, timeAgo } from '../format';
import {
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  HealthBar,
  Loading,
  Modal,
  StatusPill,
  Table,
  Toggle,
} from '../components/ui';
import { ResourceDrawer } from '../components/ResourceDrawer';
import type { Page, ProxyTestResult, ResourcePool, ResourceSummary } from '../types';

/** Çıkış havuzu. Test, proxy'nin gerçekten trafik taşıdığını ve hangi IP'yi gösterdiğini kanıtlar. */
export function Proxies() {
  const proxies = useLoad(() =>
    api.get<Page<ResourceSummary>>('/v1/resources?type=PROXY&limit=100'),
  );
  const pools = useLoad(() => api.get<ResourcePool[]>('/v1/resource-pools?type=PROXY'));

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ResourceSummary | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<ResourceSummary | null>(null);
  const [tests, setTests] = useState<Record<string, ProxyTestResult | { error: string }>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = (work: Promise<unknown>) => {
    setError(null);
    work
      .then(() => proxies.reload())
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      );
  };

  /**
   * Kopya sunucuda çıkarılıyor: parola şifreli duruyor ve panele hiç gelmiyor, dolayısıyla
   * "her şeyi kopyala" ancak orada gerçekleşebilir. Kopya çıkar çıkmaz düzenleme penceresi
   * açılıyor — kopyalamanın amacı zaten ufak bir alanı değiştirmek.
   */
  const duplicate = (proxy: ResourceSummary) => {
    setCopying(proxy.id);
    setError(null);
    api
      .post<ResourceSummary>(`/v1/proxies/${proxy.id}/duplicate`)
      .then((copy) => {
        proxies.reload();
        setEditing(copy);
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setCopying(null));
  };

  const runTest = (proxy: ResourceSummary) => {
    setTesting(proxy.id);
    api
      .post<ProxyTestResult>(`/v1/proxies/${proxy.id}/test`)
      .then((result) => setTests((prev) => ({ ...prev, [proxy.id]: result })))
      .catch((reason: unknown) =>
        setTests((prev) => ({
          ...prev,
          [proxy.id]: { error: reason instanceof Error ? reason.message : String(reason) },
        })),
      )
      .finally(() => {
        setTesting(null);
        proxies.reload();
      });
  };

  const rows = proxies.data?.items ?? [];
  const poolOptions = pools.data ?? [];
  const poolName = (id: string | null) =>
    id ? (poolOptions.find((p) => p.id === id)?.name ?? id) : '—';

  return (
    <>
      <Card
        title={`Proxy'ler${rows.length ? ` (${rows.length})` : ''}`}
        flush
        actions={
          <button type="button" onClick={() => setCreating(true)}>
            Proxy ekle
          </button>
        }
        footer="Bir proxy'ye hâlâ kimlik bağlıyken emekli edilemez — hesabın çıkış adresini oturum ortasında değiştirmek, onu ele geçirilmiş gibi gösteren şeydir."
      >
        {(error ?? proxies.error) && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={error ?? proxies.error} />
          </div>
        )}
        {proxies.loading && rows.length === 0 && <Loading />}
        {!proxies.loading && rows.length === 0 && (
          <Empty title="Tanımlı proxy yok">Kimlikler proxy olmadan doğrudan çıkış kullanır.</Empty>
        )}
        {rows.length > 0 && (
          <Table
            head={[
              'Ad',
              'Adres',
              'Durum',
              'Sağlık',
              'Çıkış IP',
              'Gecikme',
              'Havuz',
              'Son kontrol',
              '',
            ]}
          >
            {rows.map((proxy) => {
              const test = tests[proxy.id];
              const latency = metaText(proxy.metadata, 'latencyMs');
              return (
                <tr key={proxy.id} className={proxy.deactivatedAt ? 'row-retired' : undefined}>
                  <td>
                    <button type="button" className="linky" onClick={() => setInspecting(proxy)}>
                      {proxy.name}
                    </button>
                    {test && (
                      <div
                        className={'error' in test || !test.ok ? 'test-line bad' : 'test-line good'}
                      >
                        {'error' in test
                          ? test.error
                          : test.ok
                            ? `erişilebilir · ${test.externalIp ?? 'IP yok'}`
                            : `erişilemiyor · ${test.errorCode ?? 'bilinmiyor'}`}
                        {/* Erişilebilir olmak yetmiyor. Ölçüldü: satın alınan üç "temiz
                            ISP" adresinden ikisi, bizim tek isteğimiz gitmeden önce
                            yukarı akış tarafından kısıtlanmıştı — üçü de bu testi
                            geçiyordu, ve üzerlerindeki hesaplar saatler içinde düştü. */}
                        {!('error' in test) && test.upstreamVerdict === 'restricted' && (
                          <div className="test-why">
                            Bu adres yukarı akış tarafından <b>kısıtlanıyor</b> (HTTP{' '}
                            {test.upstreamStatus}) — istek hiçbir hesap taşımıyordu, yani
                            reddi hak eden tek şey adresin kendisi. Buraya hesap bağlama;
                            satıcıdan değişim iste.
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Adres API tarafından maskelenir; kimlik bilgisi metadata'ya hiç girmez. */}
                  <td className="mono muted">
                    {metaText(proxy.metadata, 'protocol') ?? 'http'}://
                    {metaText(proxy.metadata, 'hostMasked') ??
                      metaText(proxy.metadata, 'host') ??
                      '—'}
                    :{metaText(proxy.metadata, 'port') ?? '—'}
                  </td>
                  <td>
                    <StatusPill status={proxy.status} />
                  </td>
                  <td>
                    <HealthBar score={proxy.health.healthScore} />
                  </td>
                  <td className="mono muted">{metaText(proxy.metadata, 'externalIp') ?? '—'}</td>
                  <td className="muted">{latency ? duration(Number(latency)) : '—'}</td>
                  <td className="muted small">{poolName(proxy.poolId)}</td>
                  <td className="muted" title={stamp(proxy.health.lastHealthAt)}>
                    {timeAgo(proxy.health.lastHealthAt)}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="tiny"
                      disabled={testing === proxy.id}
                      onClick={() => runTest(proxy)}
                    >
                      {testing === proxy.id ? 'Test ediliyor…' : 'Test'}
                    </button>
                    <select
                      value={proxy.poolId ?? ''}
                      onChange={(e) =>
                        act(
                          e.target.value
                            ? api.post(`/v1/resources/${proxy.id}/pool/${e.target.value}`)
                            : api.post(`/v1/resources/${proxy.id}/pool/leave`),
                        )
                      }
                    >
                      <option value="">havuz yok</option>
                      {poolOptions.map((pool) => (
                        <option key={pool.id} value={pool.id}>
                          {pool.name}
                        </option>
                      ))}
                    </select>
                    <Toggle
                      on={proxy.status !== 'DISABLED'}
                      label={`${proxy.name} kullanımda`}
                      onChange={(next) =>
                        act(api.post(`/v1/resources/${proxy.id}/${next ? 'enable' : 'disable'}`))
                      }
                    />
                    <button type="button" className="tiny" onClick={() => setEditing(proxy)}>
                      Düzenle
                    </button>
                    <button
                      type="button"
                      className="tiny"
                      disabled={copying === proxy.id}
                      title="Kimlik bilgisi dahil birebir kopya çıkarır; kopya kapalı doğar"
                      onClick={() => duplicate(proxy)}
                    >
                      {copying === proxy.id ? 'Kopyalanıyor…' : 'Kopyala'}
                    </button>
                    <ConfirmButton
                      label="Emekli et"
                      confirmLabel="Emin misin?"
                      onConfirm={() => act(api.post(`/v1/resources/${proxy.id}/deactivate`))}
                    />
                    {/* Silme, emekli etmenin yanında ayrı duruyor: emekli etmek geri
                        alınabilir, bu alınamaz. Kiralama geçmişi olan kaynağı sunucu
                        zaten reddediyor — o iz, bir çalışmanın hangi çıkıştan gittiğinin
                        tek kaydı. */}
                    <ConfirmButton
                      label="Sil"
                      confirmLabel="Kalıcı olarak sil?"
                      onConfirm={() => act(api.del(`/v1/resources/${proxy.id}`))}
                    />
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      {creating && (
        <CreateProxy
          pools={poolOptions}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            proxies.reload();
          }}
        />
      )}
      {editing && (
        <EditProxy
          proxy={editing}
          pools={poolOptions}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            proxies.reload();
          }}
        />
      )}
      {inspecting && <ResourceDrawer resource={inspecting} onClose={() => setInspecting(null)} />}
    </>
  );
}

function CreateProxy({
  pools,
  onClose,
  onDone,
}: {
  pools: ResourcePool[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: '',
    host: '',
    port: '8080',
    protocol: 'http',
    username: '',
    password: '',
    country: '',
    kind: 'unknown',
    poolId: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = () => {
    setBusy(true);
    setError(null);
    api
      .post('/v1/proxies', {
        name: form.name,
        host: form.host,
        port: Number(form.port),
        protocol: form.protocol,
        kind: form.kind,
        ...(form.username ? { username: form.username } : {}),
        ...(form.password ? { password: form.password } : {}),
        ...(form.country ? { country: form.country } : {}),
        ...(form.poolId ? { poolId: form.poolId } : {}),
      })
      .then(onDone)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Proxy ekle"
      submitLabel="Oluştur"
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      error={error}
    >
      <Field label="Ad">
        <input value={form.name} onChange={(e) => set('name')(e.target.value)} required />
      </Field>
      <div className="field-row">
        <Field label="Sunucu">
          <input value={form.host} onChange={(e) => set('host')(e.target.value)} required />
        </Field>
        <Field label="Port">
          <input
            type="number"
            min={1}
            max={65535}
            value={form.port}
            onChange={(e) => set('port')(e.target.value)}
            required
          />
        </Field>
        <Field label="Protokol">
          <select value={form.protocol} onChange={(e) => set('protocol')(e.target.value)}>
            <option value="http">http</option>
            <option value="https">https</option>
            <option value="socks5">socks5</option>
          </select>
        </Field>
      </div>
      <div className="field-row">
        <Field label="Kullanıcı adı" hint="İsteğe bağlı.">
          <input
            value={form.username}
            autoComplete="off"
            onChange={(e) => set('username')(e.target.value)}
          />
        </Field>
        <Field label="Parola" hint="Geldiği anda şifrelenir.">
          <input
            type="password"
            value={form.password}
            autoComplete="new-password"
            onChange={(e) => set('password')(e.target.value)}
          />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Ülke" hint="Serbest metin, kendi filtrelemen için.">
          <input value={form.country} onChange={(e) => set('country')(e.target.value)} />
        </Field>
        <Field label="Tür">
          <select value={form.kind} onChange={(e) => set('kind')(e.target.value)}>
            {Object.entries(PROXY_KINDS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Havuz">
          <select value={form.poolId} onChange={(e) => set('poolId')(e.target.value)}>
            <option value="">havuz yok</option>
            {pools.map((pool) => (
              <option key={pool.id} value={pool.id}>
                {pool.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <p className="note">
        SOCKS5 proxy'ler saklanıp kullanılabilir, ancak yerleşik test yalnızca HTTP konuşur.
      </p>
    </Modal>
  );
}

const PROXY_KINDS: Record<string, string> = {
  residential: 'konut',
  datacenter: 'veri merkezi',
  isp: 'ISP',
  mobile: 'mobil',
  unknown: 'bilinmiyor',
};

/**
 * Var olan bir proxy'yi düzenler.
 *
 * Parola alanı BOŞ açılıyor ve boş bırakılırsa dokunulmuyor. Saklanan parolayı forma
 * doldurmak, onu her düzenlemede tarayıcıya ve her ekran görüntüsüne taşımak olurdu;
 * hiç göstermemek de "parolayı sildim mi?" sorusunu doğurur — bu yüzden alanın altında
 * ne olacağı yazıyor.
 */
function EditProxy({
  proxy,
  pools,
  onClose,
  onDone,
}: {
  proxy: ResourceSummary;
  pools: ResourcePool[];
  onClose: () => void;
  onDone: () => void;
}) {
  const meta = (key: string) => metaText(proxy.metadata, key) ?? '';
  const [form, setForm] = useState({
    name: proxy.name,
    host: meta('host'),
    port: meta('port') || '8080',
    protocol: meta('protocol') || 'http',
    username: '',
    password: '',
    country: meta('country'),
    city: meta('city'),
    providerName: meta('providerName'),
    kind: meta('kind') || 'unknown',
    poolId: proxy.poolId ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const hasCredentials = proxy.metadata.hasCredentials === true;

  const submit = () => {
    setBusy(true);
    setError(null);
    // Yalnız DEĞİŞEN alanlar gönderiliyor. Her alanı her seferinde göndermek, boş bırakılan
    // bir kimlik bilgisini "sil" gibi okutma riskini taşırdı.
    const patch: Record<string, unknown> = {};
    if (form.name !== proxy.name) patch.name = form.name;
    if (form.host !== meta('host')) patch.host = form.host;
    if (form.port !== meta('port')) patch.port = Number(form.port);
    if (form.protocol !== (meta('protocol') || 'http')) patch.protocol = form.protocol;
    if (form.kind !== (meta('kind') || 'unknown')) patch.kind = form.kind;
    for (const field of ['country', 'city', 'providerName'] as const) {
      if (form[field] !== meta(field) && form[field]) patch[field] = form[field];
    }
    if (form.username) patch.username = form.username;
    if (form.password) patch.password = form.password;

    const work =
      form.poolId === (proxy.poolId ?? '')
        ? Promise.resolve()
        : form.poolId
          ? api.post(`/v1/resources/${proxy.id}/pool/${form.poolId}`)
          : api.post(`/v1/resources/${proxy.id}/pool/leave`);

    work
      .then(() =>
        Object.keys(patch).length ? api.patch(`/v1/proxies/${proxy.id}`, patch) : undefined,
      )
      .then(onDone)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={`Proxy düzenle — ${proxy.name}`}
      submitLabel="Kaydet"
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      error={error}
    >
      <Field label="Ad">
        <input value={form.name} onChange={(e) => set('name')(e.target.value)} required />
      </Field>
      <div className="field-row">
        <Field label="Sunucu">
          <input value={form.host} onChange={(e) => set('host')(e.target.value)} required />
        </Field>
        <Field label="Port">
          <input
            type="number"
            min={1}
            max={65535}
            value={form.port}
            onChange={(e) => set('port')(e.target.value)}
            required
          />
        </Field>
        <Field label="Protokol">
          <select value={form.protocol} onChange={(e) => set('protocol')(e.target.value)}>
            <option value="http">http</option>
            <option value="https">https</option>
            <option value="socks5">socks5</option>
          </select>
        </Field>
      </div>
      <div className="field-row">
        <Field
          label="Kullanıcı adı"
          hint={hasCredentials ? 'Boş bırakırsan değişmez.' : 'İsteğe bağlı.'}
        >
          <input
            value={form.username}
            autoComplete="off"
            placeholder={hasCredentials ? '(kayıtlı)' : ''}
            onChange={(e) => set('username')(e.target.value)}
          />
        </Field>
        <Field
          label="Parola"
          hint={hasCredentials ? 'Boş bırakırsan değişmez.' : 'Geldiği anda şifrelenir.'}
        >
          <input
            type="password"
            value={form.password}
            autoComplete="new-password"
            placeholder={hasCredentials ? '(kayıtlı)' : ''}
            onChange={(e) => set('password')(e.target.value)}
          />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Ülke">
          <input value={form.country} onChange={(e) => set('country')(e.target.value)} />
        </Field>
        <Field label="Şehir">
          <input value={form.city} onChange={(e) => set('city')(e.target.value)} />
        </Field>
        <Field label="Sağlayıcı">
          <input value={form.providerName} onChange={(e) => set('providerName')(e.target.value)} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Tür">
          <select value={form.kind} onChange={(e) => set('kind')(e.target.value)}>
            {Object.entries(PROXY_KINDS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Havuz">
          <select value={form.poolId} onChange={(e) => set('poolId')(e.target.value)}>
            <option value="">havuz yok</option>
            {pools.map((pool) => (
              <option key={pool.id} value={pool.id}>
                {pool.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {proxy.status === 'DISABLED' && (
        <p className="note">
          Bu proxy şu an kapalı. Kopyalar bilerek kapalı doğuyor — açmak için satırdaki anahtarı
          kullan.
        </p>
      )}
    </Modal>
  );
}
